import { classes } from 'html-classes';
import { observer } from 'mobx-react-lite';
import { Fragment } from 'react/jsx-runtime';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { ETADeliveryMethodType } from '~/api/ETADeliveryMethodType';
import { company } from '~/company/Company';
import Collapsible from '~/components/Collapsible/Collapsible';
import Icon from '~/components/Icon/Icon';
import { ModalType } from '~/components/Modal/interface';
import Price from '~/components/Price';
import Spinner from '~/components/Spinner/Spinner';
import { useCheckoutButton } from '~/hooks/useCheckoutButton';
import { useModal } from '~/hooks/useModal';
import { useIsTablet } from '~/pages/Order/hooks';
import {
  cartStore,
  checkoutStore,
  mainStore,
  orderStore,
  userStore,
} from '~/stores';
import { awsPaymentStore } from '~/stores/AwsPaymentStore';
import type { CartItem, CartItemOffer } from '~/stores/CartStore';
import { formatPriceWithCurrency } from '~/utils/formaters';

import styles from './Total.module.scss';

const Total = () => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const isTablet = useIsTablet();
  const { isDisabled, onClick, text, description, isLoading } =
    useCheckoutButton();

  const isLoadingConfirm =
    isLoading ||
    checkoutStore.isLoading ||
    orderStore.isLoading ||
    awsPaymentStore.isIframeLoading;

  const isPromoLoading = cartStore.promotionStore.applyPromocodeMeta.isLoading;

  const propsStirng = (i: CartItem) => {
    return (i as CartItemOffer).selectableProperties &&
      Object.keys((i as CartItemOffer).selectableProperties).length > 0
      ? `, ${Object.values((i as CartItemOffer).selectableProperties)
          .map((i) => i.value)
          .join(', ')}`
      : '';
  };

  const openDeliveryModal = () => {
    openModal(ModalType.DeliveryInfo, 'checkout');
  };

  const totalDeliveryFee = cartStore.deliveriesOrders.reduce(
    (acc, { is_delivery_free, delivery_info: { paid_price } }) => {
      if (is_delivery_free) {
        return acc;
      }
      return acc + paid_price;
    },
    0,
  );

  const hasExpressDelivery = checkoutStore.deliveries?.some(
    (delivery) => !delivery.slotDeliveryDetails,
  );

  const minimalOrderValueError =
    cartStore.totalCartPrice.minimal_order_value_error?.length &&
    cartStore.totalCartPrice.minimal_order_value_error[0] ? (
      <>
        {t('errors:minCartError', {
          amount: formatPriceWithCurrency(
            cartStore.totalCartPrice.minimal_order_value_error[0]
              ?.minimalOrderValue / 100,
          ),
        })}
        <br />
        {t('errors:minCartErrorDetails', {
          amount: formatPriceWithCurrency(
            (cartStore.totalCartPrice.minimal_order_value_error[0]
              ?.minimalOrderValue -
              cartStore.totalCartPrice.minimal_order_value_error[0]
                ?.orderPriceWithoutDelivery) /
              100,
          ),
        })}
      </>
    ) : null;

  return (
    <div className={styles.totalPanel}>
      <>
        {checkoutStore.isLoading ||
          orderStore.isLoading ||
          (isPromoLoading && <Spinner layout={true} />)}
        <div className={styles.checkoutSummaryItemContainer}>
          <div className={styles['checkout-summary__item']}>
            <Collapsible
              dataAttributeName="items"
              titleElem={
                <div className={styles['checkout-summary__header']}>
                  <div className={styles['checkout-summary__header-text']}>
                    <p
                      className={styles['checkout-summary__header-text-title']}
                    >
                      {t('subtotal')}
                      <Icon type="chevron" size={16} />
                    </p>
                  </div>
                  <Price
                    price={cartStore.totalCartPrice.paidWithoutDiscount}
                    classNameCurrentPrice={
                      styles['checkout-summary__header-price']
                    }
                  />
                </div>
              }
              isCollapsed={true}
            >
              <ul className={styles['checkout-summary__list']}>
                {cartStore.selectedCartItems.map((i) => (
                  <li
                    key={i.id}
                    className={styles['checkout-summary__list-item']}
                  >
                    <div className={styles['checkout-summary__list-item-info']}>
                      {i.name}
                      {propsStirng(i)}{' '}
                      {i.count > 1 && (
                        <span
                          className={
                            styles[
                              'checkout-summary__list-item-info-product-count'
                            ]
                          }
                        >
                          {i.count}x
                        </span>
                      )}
                    </div>
                    <div
                      className={styles['checkout-summary__list-item-price']}
                    >
                      {formatPriceWithCurrency(
                        (i.discountPrice
                          ? i.count * i.discountPrice
                          : i.count * i.price) / 100,
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Collapsible>
          </div>
          {checkoutStore.deliveryMethod !==
            ETADeliveryMethodType.ClickAndCollect && (
            <div className={styles['checkout-summary__item']}>
              <Collapsible
                dataAttributeName="delivery-fee"
                titleElem={
                  <div className={styles['checkout-summary__header']}>
                    <div className={styles['checkout-summary__header-text']}>
                      <p
                        className={
                          styles['checkout-summary__header-text-title']
                        }
                      >
                        {t('deliveryFeeLong')}{' '}
                        {company.isDeliveryInfoModalEnabled && (
                          <div
                            data-tooltip="info"
                            onClick={openDeliveryModal}
                            className={styles.deliveryTooltip}
                          >
                            <Icon className="static" type="info" size={16} />
                          </div>
                        )}
                        <Icon type="chevron" size={16} />
                      </p>
                    </div>
                    <Price
                      price={mainStore.convertPenceToPounds(totalDeliveryFee)}
                      classNameCurrentPrice={
                        styles['checkout-summary__header-price']
                      }
                    />
                  </div>
                }
                isCollapsed={true}
              >
                <ul className={styles['checkout-summary__list']}>
                  {checkoutStore.deliveries?.map((delivery, index) => {
                    const key = delivery.slotDeliveryDetails
                      ? delivery.slotDeliveryDetails?.scheduleSlotId +
                        delivery.slotDeliveryDetails?.currentDate
                      : 'express';

                    const order = cartStore.deliveriesOrders[index];
                    const isDeliveryFree = order?.is_delivery_free;
                    const deliveryPrice = formatPriceWithCurrency(
                      mainStore.convertPenceToPounds(
                        order?.delivery_info.paid_price ?? 0,
                      ),
                    );
                    const slotNumber = hasExpressDelivery ? index : index + 1;

                    if (!order) {
                      return <Fragment key={key} />;
                    }

                    const items = delivery.itemsIds.reduce(
                      (acc, i) =>
                        acc +
                        (cartStore.cart.find((c) => c.sku === i)?.count ?? 0),
                      0,
                    );

                    return (
                      <li
                        key={key}
                        className={styles['checkout-summary__list-item']}
                      >
                        <div
                          className={styles['checkout-summary__list-item-info']}
                        >
                          {!slotNumber
                            ? t('expressDelivery')
                            : t('slot', { num: slotNumber })}{' '}
                          <span
                            className={
                              styles[
                                'checkout-summary__list-item-info-item-count'
                              ]
                            }
                          >
                            {items} {t(items > 1 ? 'items' : 'item')}
                          </span>
                        </div>
                        <div
                          className={
                            styles['checkout-summary__list-item-price']
                          }
                        >
                          {isDeliveryFree ? (
                            <p className="free">{t('deliveryFreeShort')}</p>
                          ) : (
                            <p className={styles['slot-list__slot-fee']}>
                              {deliveryPrice}
                            </p>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </Collapsible>
            </div>
          )}
          <div className={styles['checkout-summary__item']}>
            <ul className={styles['checkout-taxes__list']}>
              {!cartStore.totalCartPrice.taxPercent &&
              !cartStore.totalCartPrice.taxAmount ? null : (
                <li className={styles['checkout-taxes__list-item']}>
                  <div className={styles['checkout-taxes__list-item-title']}>
                    <p className="">
                      {cartStore.totalCartPrice.taxPercent > 0
                        ? t('phrases:vatCheckout', {
                            percent: cartStore.totalCartPrice.taxPercent,
                          })
                        : t('phrases:vat_')}
                    </p>
                  </div>
                  <Price
                    price={mainStore.convertPenceToPounds(
                      cartStore.totalCartPrice.taxAmount,
                    )}
                    classNameCurrentPrice={
                      styles['checkout-taxes__list-item-price']
                    }
                  />
                </li>
              )}
              {cartStore.promocode.success === true && (
                <li className={styles['checkout-taxes__list-item']}>
                  <div className={styles['checkout-taxes__promocode-code']}>
                    <p
                      className={styles['checkout-taxes__promocode-code-title']}
                    >
                      {t('promocode')}
                    </p>
                    <p
                      className={styles['checkout-taxes__promocode-code-value']}
                    >
                      {cartStore.promocode.value}
                    </p>
                  </div>
                  <Price
                    price={`-${cartStore.totalCartPrice.promocodeDiscount}`}
                    classNameCurrentPrice={
                      styles['checkout-taxes__promocode-discount']
                    }
                  />
                </li>
              )}
              {userStore.loyaltyPointsStore.useLoyaltyPoints &&
                cartStore.totalCartPrice.paidLoyaltyPointsPence > 0 && (
                  <li className={styles['checkout-taxes__list-item']}>
                    <div className={styles['checkout-taxes__promocode-code']}>
                      <p
                        className={
                          styles['checkout-taxes__promocode-code-title']
                        }
                      >
                        {t('loyalty:checkout:points')}
                      </p>
                      <p
                        className={
                          styles['checkout-taxes__promocode-code-points']
                        }
                      >
                        {`-${cartStore.totalCartPrice.paidLoyaltyPointsPence}`}
                      </p>
                    </div>
                    <Price
                      price={`-${cartStore.totalCartPrice.loyaltyPointsDiscount}`}
                      classNameCurrentPrice={
                        styles['checkout-taxes__promocode-discount']
                      }
                    />
                  </li>
                )}
            </ul>
          </div>
        </div>
        <div className={styles['checkout-summary__description-total']}>
          <p className={styles['checkout-summary__description-total-text']}>
            {t('total')}
          </p>
          <Price
            price={cartStore.totalCartPrice.paid}
            classNameCurrentPrice={
              styles['checkout-summary__description-total-price']
            }
          />
        </div>

        {!isTablet && (
          <div className={styles.buttonContainer}>
            {!company.buttonOutPanelCheckout &&
              !awsPaymentStore.isActiveAwsIframe && (
                <button
                  data-button="confirm-order"
                  className={classes([
                    'button _primary _big',
                    styles['checkout-summary__button'],
                    isLoadingConfirm &&
                      styles['checkout-summary__button_loading'],
                  ])}
                  disabled={isDisabled || !!minimalOrderValueError}
                  onClick={onClick}
                >
                  {isLoadingConfirm && <span className="spinner _white" />}
                  {!minimalOrderValueError ? text : minimalOrderValueError}
                  {!minimalOrderValueError && description?.length > 0 && (
                    <span
                      className={
                        styles['checkout-summary__button__description']
                      }
                    >
                      {description}
                    </span>
                  )}
                </button>
              )}
            <p className={styles['checkout-summary__description-text-terms']}>
              {t('phrases:byOrdering')}{' '}
              <Link
                data-link="terms"
                to={'/legals'}
                state={{ source: 'checkout' }}
              >
                {t('termsAndConditions')}
              </Link>
            </p>
          </div>
        )}
      </>
    </div>
  );
};

export default observer(Total);
